/**
 * « Passages » (IELTS, D-105): the Listening parts (script + audio) and the Reading sections
 * (texts) shared by the questions of a mock. Audios are generated on the server in English
 * (Piper, background task), like the TCF CO audios: « generate the audio » per part and
 * « generate the missing audios » for all. A new audio sends the part back to draft, so a trainer
 * listens to it before publishing it again.
 */

import {
  type Passage,
  type QuestionSkillEnum,
  useAcademyPassagesAudioStatusRetrieve,
  useAcademyPassagesGenerateAudioCreate,
  useAcademyPassagesGenerateMissingAudio,
  useAcademyPassagesList,
  useAcademyPassagesPublishCreate,
  useAcademyPassagesRetrieve,
  useAcademyPassagesReviewCreate,
} from "@agencyos/api-client";
import { Badge, Button, EmptyState, GlassCard, GlassSheet, SectionHeader, SelectField, Skeleton } from "@agencyos/ui-web";
import { AudioLines, BookText, Headphones, Wand2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import "./ielts-admin.css";
import { CoAudioPlayer } from "./CoAudioTools";
import { IELTS, useAdminExam } from "./examContext";
import { ExercisePreview } from "./ExercisePreview";
import { usePassageQuestions } from "./ieltsHooks";
import { useIeltsSkillName, usePassageText, ValidationPanel } from "./ieltsShared";
import { OriginalContentBanner } from "./QuestionsTab";
import { CONTENT_STATUSES, ErrorList, isObject, Pager, percent, StatusBadge, str, useAcademyRoles, useRefreshAcademy } from "./shared";

const SKILLS: QuestionSkillEnum[] = ["co", "ce"];
/** While a generation runs, the counter and the list are refreshed every few seconds. */
const POLL_MS = 4000;

/** The counter of Listening audios and the « generate the missing audios » button. */
export function PassageAudioSummary() {
  const { t } = useTranslation();
  const pt = usePassageText();
  const { canEdit } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const [waiting, setWaiting] = useState(false);
  const { params } = useAdminExam();
  const status = useAcademyPassagesAudioStatusRetrieve(params, {
    query: {
      refetchInterval: (query) => {
        const data = query.state.data;
        return waiting && !(data?.status === 200 && data.data.without_audio === 0) ? POLL_MS : false;
      },
    },
  });
  const withAudio = status.data?.status === 200 ? status.data.data.with_audio : undefined;
  useEffect(() => {
    if (waiting && withAudio !== undefined) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the counter moves
  }, [withAudio]);
  const generate = useAcademyPassagesGenerateMissingAudio({
    mutation: {
      onSuccess: (r) => {
        if (r.status !== 202) return;
        setWaiting(true);
        refresh();
      },
    },
  });
  const data = status.data?.status === 200 ? status.data.data : undefined;
  if (!data) return null;
  const queued = generate.data?.status === 202 ? generate.data.data.queued : null;
  return (
    <section className="stack co-audio" aria-labelledby="ielts-audio-title">
      <div className="row co-audio__head">
        <AudioLines size={18} strokeWidth={1.75} aria-hidden="true" />
        <strong id="ielts-audio-title">{t(pt("audioTitle"))}</strong>
        <span role="status" className="muted">
          {t("academyAudio.counter", { with: data.with_audio, without: data.without_audio })}
        </span>
        {data.configured && data.mode === "sandbox" ? <Badge>{t("academyAudio.sandbox")}</Badge> : null}
      </div>
      {!data.configured ? (
        <p role="note" className="notice-warning">
          {t("academyAudio.notConfigured")}
        </p>
      ) : null}
      {canEdit ? (
        <div className="row">
          <Button
            variant="secondary"
            icon={<Wand2 size={16} strokeWidth={1.75} aria-hidden="true" />}
            disabled={!data.configured || data.without_audio === 0 || generate.isPending}
            onClick={() => generate.mutate({ params })}
          >
            {generate.isPending ? t("common.loading") : t("academyAudio.generateMissing")}
          </Button>
          {data.without_audio === 0 ? <span className="muted">{t("academyAudio.nothingMissing")}</span> : null}
          {queued !== null ? (
            <span role="status" className="ok">
              {t("academyAudio.queued", { count: queued })}
            </span>
          ) : null}
        </div>
      ) : null}
      <p className="muted">{t(pt("audioNote"))}</p>
      <ErrorList error={generate.error} />
    </section>
  );
}

/** Player and « generate the audio » button of one Listening part. */
function PassageAudio({ passage }: { passage: Passage }) {
  const { t } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const generate = useAcademyPassagesGenerateAudioCreate({ mutation: { onSuccess: refresh } });
  if (passage.skill !== "co") return null;
  return (
    <div className="stack co-audio__row ielts-start">
      {passage.audio_url ? <CoAudioPlayer src={passage.audio_url} /> : <Badge tone="danger">{t("academyAdmin.questions.noAudioShort")}</Badge>}
      {canEdit ? (
        <div className="row">
          <Button variant="ghost" size="sm" icon={<Wand2 size={16} strokeWidth={1.75} aria-hidden="true" />} disabled={generate.isPending} onClick={() => generate.mutate({ id: passage.id })}>
            {passage.audio_url ? t("academyAudio.regenerate") : t("academyAudio.generateOne")}
          </Button>
          {generate.data?.status === 202 ? (
            <span role="status" className="ok">
              {t("academyAudio.queued", { count: 1 })}
            </span>
          ) : null}
        </div>
      ) : null}
      <ErrorList error={generate.error} />
    </div>
  );
}

function textsOf(value: unknown): { title: string; text: string }[] {
  if (!Array.isArray(value)) return [];
  return (value as unknown[]).filter(isObject).flatMap((row) => {
    const text = str(row.text);
    return text ? [{ title: str(row.title), text }] : [];
  });
}

function PassageQuestions({ passage }: { passage: Passage }) {
  const { t } = useTranslation();
  const pt = usePassageText();
  const questions = usePassageQuestions(IELTS, passage.skill, passage.id);
  return (
    <section className="stack" aria-labelledby={`passage-questions-${passage.id}`}>
      <h3 id={`passage-questions-${passage.id}`} className="ao-headline">
        {t(pt("questions"), { count: passage.questions })}
      </h3>
      {questions.data === undefined ? (
        questions.isError ? <p className="ko">{t("common.error")}</p> : <Skeleton style={{ height: 120 }} />
      ) : questions.data.length ? (
        <ol className="ielts-exercises">
          {questions.data.map((q, i) => (
            <li key={q.id}>
              <ExercisePreview
                index={q.number ?? i + 1}
                exercise={{ id: q.id, type: q.question_type ?? "mcq", prompt: q.stem, data: isObject(q.data) ? { options: q.options, ...q.data } : { options: q.options }, answer: !q.question_type || q.question_type === "mcq" ? q.answer : q.accepted, explanation: q.explanation ?? "" }}
              />
            </li>
          ))}
        </ol>
      ) : (
        <p className="muted">{t(pt("noQuestion"))}</p>
      )}
    </section>
  );
}

function PassageSheet({ passage, onClose }: { passage: Passage; onClose: () => void }) {
  const { t } = useTranslation();
  const pt = usePassageText();
  const { canEdit } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const skillName = useIeltsSkillName();
  const detail = useAcademyPassagesRetrieve(passage.id);
  const [current, setCurrent] = useState<Passage | null>(null);
  const loaded = current ?? (detail.data?.status === 200 ? detail.data.data : passage);
  const review = useAcademyPassagesReviewCreate({ mutation: { onSuccess: refresh } });
  const publish = useAcademyPassagesPublishCreate({ mutation: { onSuccess: refresh } });
  const texts = textsOf(loaded.texts);
  return (
    <GlassSheet open title={t(loaded.skill === "co" ? pt("partN") : pt("sectionN"), { skill: skillName(loaded.skill), n: loaded.number })} onClose={onClose}>
      <div className="stack">
        <div className="row">
          <StatusBadge status={loaded.status} />
          {loaded.level ? <Badge>{loaded.level}</Badge> : null}
          <span className="muted">{t("academyAdmin.similarity", { value: percent(loaded.similarity) })}</span>
        </div>
        <OriginalContentBanner />
        {loaded.title ? (
          <p lang="en" className="ao-title-3">
            {loaded.title}
          </p>
        ) : null}
        {loaded.context ? (
          <p lang="en" className="muted">
            {loaded.context}
          </p>
        ) : null}
        {loaded.skill === "co" ? (
          <section className="stack" aria-labelledby={`passage-audio-${loaded.id}`}>
            <h3 id={`passage-audio-${loaded.id}`} className="ao-headline">
              {t("academyAdmin.questions.audio")}
            </h3>
            <PassageAudio passage={loaded} />
          </section>
        ) : null}
        <section className="stack" aria-labelledby={`passage-text-${loaded.id}`}>
          <h3 id={`passage-text-${loaded.id}`} className="ao-headline">
            {loaded.skill === "co" ? t(pt("script")) : t(pt("texts"))}
          </h3>
          {loaded.document ? (
            <p lang="en" className="ao-surface ao-card academy-pre ielts-document">
              {loaded.document}
            </p>
          ) : null}
          {texts.map((text, i) => (
            <article key={`${text.title}-${i}`} className="ao-surface ao-card stack ielts-document" lang="en">
              {text.title ? <strong>{text.title}</strong> : null}
              <p className="academy-pre">{text.text}</p>
            </article>
          ))}
          {!loaded.document && !texts.length ? <p className="muted">{t("academyAdmin.ielts.course.none")}</p> : null}
        </section>
        <PassageQuestions passage={loaded} />
        {canEdit ? (
          <ValidationPanel
            id={`passage-validation-${loaded.id}`}
            status={loaded.status}
            pending={review.isPending || publish.isPending}
            reviewError={review.error}
            publishError={publish.error}
            done={review.isSuccess || publish.isSuccess}
            onReview={() => {
              publish.reset();
              review.mutate({ id: loaded.id }, { onSuccess: (r) => r.status === 200 && setCurrent(r.data) });
            }}
            onPublish={() => {
              review.reset();
              publish.mutate({ id: loaded.id }, { onSuccess: (r) => r.status === 200 && setCurrent(r.data) });
            }}
          />
        ) : null}
      </div>
    </GlassSheet>
  );
}

export function PassagesTab() {
  const { t } = useTranslation();
  const pt = usePassageText();
  const skillName = useIeltsSkillName();
  const { params } = useAdminExam();
  const [skill, setSkill] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<Passage | null>(null);
  const close = useCallback(() => setOpen(null), []);
  const list = useAcademyPassagesList({ ...params, page, skill: skill || undefined, status: status || undefined });
  const data = list.data?.status === 200 ? list.data.data : undefined;
  return (
    <GlassCard as="section" className="stack" aria-labelledby="ielts-passages-title">
      <SectionHeader id="ielts-passages-title" title={t(pt("title"))} subtitle={t(pt("intro"))} />
      <OriginalContentBanner />
      <PassageAudioSummary />
      <div className="form-grid academy-filters">
        <SelectField
          label={t("academyAdmin.skillLabel")}
          value={skill}
          options={[{ value: "", label: t("academyAdmin.all") }, ...SKILLS.map((s) => ({ value: s, label: skillName(s) }))]}
          onChange={(e) => {
            setSkill(e.target.value);
            setPage(1);
          }}
        />
        <SelectField
          label={t("academyAdmin.statusLabel")}
          value={status}
          options={[{ value: "", label: t("academyAdmin.all") }, ...CONTENT_STATUSES.map((s) => ({ value: s, label: t(`academyAdmin.status.${s}`) }))]}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        />
      </div>
      {data === undefined ? (
        <Skeleton style={{ height: 200 }} />
      ) : data.results.length ? (
        <>
          <p className="muted">{t("academyAdmin.count", { count: data.count })}</p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t(pt("part"))}</th>
                  <th>{t(pt("passageTitle"))}</th>
                  <th>{t(pt("questionsShort"))}</th>
                  <th>{t("academyAdmin.questions.audio")}</th>
                  <th>{t("academyAdmin.statusLabel")}</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((p) => (
                  <tr key={p.id}>
                    <td className="ielts-nowrap">
                      <span className="row">
                        {p.skill === "co" ? <Headphones size={16} strokeWidth={1.75} aria-hidden="true" /> : <BookText size={16} strokeWidth={1.75} aria-hidden="true" />}
                        {t(p.skill === "co" ? pt("partN") : pt("sectionN"), { skill: skillName(p.skill), n: p.number })}
                      </span>
                    </td>
                    <td>
                      <button type="button" className="link" lang="en" onClick={() => setOpen(p)}>
                        {p.title || t(pt("untitled"))}
                      </button>
                    </td>
                    <td className="ao-num">{p.questions}</td>
                    <td>{p.skill === "co" ? <PassageAudio passage={p} /> : <span className="muted">—</span>}</td>
                    <td>
                      <StatusBadge status={p.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
        </>
      ) : (
        <EmptyState icon={<Headphones size={22} strokeWidth={1.75} />} title={t(pt("empty"))} description={t(pt("emptyHint"))} />
      )}
      {open ? <PassageSheet key={open.id} passage={open} onClose={close} /> : null}
    </GlassCard>
  );
}
